import React, { useEffect, useRef, useState } from "react";
import { createUUID } from "../../../utils/index";
import { useHistory, Prompt } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doUpdatePeers } from "../../../store/peers";
import styles from "./index.module.scss";
import mainSquare from "../../../logo.svg";
import ThemeSwitcher from "../../../components/ThemeSwitcher";
// import RemoteVideo from "../../../components/RemoteVideo";

// One of the following themes
import "@simonwep/pickr/dist/themes/classic.min.css"; // 'classic' theme
import "@simonwep/pickr/dist/themes/monolith.min.css"; // 'monolith' theme
import "@simonwep/pickr/dist/themes/nano.min.css"; // 'nano' theme

// window.onerror = function myErrorHandler(errorMsg, url, lineNumber) {
//   alert("Error occured: " + errorMsg); //or any message
//   return false;
// };

const PEER_DISCONNECTED_FUNC = (uuid) => {
  return JSON.stringify({
    event: "PEER_DISCONNECTED",
    uuid,
  });
};

var uuid = createUUID();
var peerConnectionConfig = {
  iceServers: [
    { urls: "stun:stun.stunprotocol.org:3478" },
    { urls: "stun:stun.l.google.com:19302" },
  ],
};
var serverConnection;

const send = function (message, callback) {
  waitForConnection(
    function () {
      try {
        serverConnection.send(message);
        if (typeof callback !== "undefined") {
          callback();
        }
      } catch (err) {
        console.error("send message failed", err);
      }
    },
    200,
    20
  );
};

const waitForConnection = function (callback, interval, retries = 20) {
  if (!serverConnection || retries <= 0) {
    return;
  }
  if (serverConnection.readyState === 1) {
    callback();
    return;
  }
  if (serverConnection.readyState !== 0) {
    return;
  }
  setTimeout(function () {
    waitForConnection(callback, interval, retries - 1);
  }, interval);
};

var localStream;
var isCaller;
var sendChannel;
var receiveChannel;
var captureStream = null;
var peerConnection;

function App() {
  const history = useHistory();
  const roomId = window.location.pathname.split("/").pop() || "";
  const roomPreviewId = roomId.slice(0, 8).toUpperCase();
  const roomSearchParams = new URLSearchParams(window.location.search);
  const selfPetType = roomSearchParams.get("selfPetType") || "";
  const peerPetType = roomSearchParams.get("peerPetType") || "";
  const hasPetTypeSummary = Boolean(selfPetType || peerPetType);
  useEffect(() => {
    serverConnection = new WebSocket( //
      window.location.origin.includes("localhost")
        ? `wss://localhost:8443${window.location.pathname}`
        : `wss://api.puipi.com${window.location.pathname}`,
      [uuid]
    );

    serverConnection.onopen = () => {
      console.log("Websocket connected [room]");
    };
  }, [uuid]);

  const localVideo = useRef(null);
  const remoteVideo = useRef(null);
  const canvasRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  const pendingRemoteOfferRef = useRef(null);
  const isLocalMediaReadyRef = useRef(false);
  const pendingStartAsCallerRef = useRef(false);
  const remoteSdpQueueRef = useRef(Promise.resolve());
  const isLeavingRef = useRef(false);
  const [peerLeft, setPeerLeft] = useState(false);
  const [hasConnectRemote, setHasConnectRemote] = useState(false);
  const [textareaValue, setTextareaValue] = useState("");
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [status, setStatus] = useState("等待對方加入...");
  const dispatch = useDispatch();
  const videoPeers = useSelector((state) => state.peers.payload) || [];

  useEffect(() => {
    isCaller = undefined;
    localStream = null;
    sendChannel = null;
    receiveChannel = null;
    captureStream = null;
    peerConnection = null;
    console.log("init");
    init();

    const handleBeforeUnload = (e) => {
      if (!isLeavingRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (serverConnection && serverConnection.readyState <= 1) {
        serverConnection.close();
      }
      if (peerConnection && peerConnection.signalingState !== "closed") {
        peerConnection.close();
      }
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      if (captureStream) {
        captureStream.getTracks().forEach((track) => track.stop());
      }
      pendingIceCandidatesRef.current = [];
      pendingRemoteOfferRef.current = null;
      isLocalMediaReadyRef.current = false;
      pendingStartAsCallerRef.current = false;
      remoteSdpQueueRef.current = Promise.resolve();
    };
  }, []);

  const init = async () => {
    initWebRTC();
    initUserMedia();
    // initCanvas(canvasRef, sendToChannel);
  };

  useEffect(() => {
    if (videoPeers.length > 1) {
      if (uuid === videoPeers[0].userId) {
        console.log("handleConnect(true)");
        setTimeout(() => {
          handleConnect(true);
        }, 500);
      }
    }
  }, [videoPeers]);

  useEffect(() => {
    if (!localStream) {
      return;
    }
    const videoTrack = localStream.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = videoEnabled;
    }
  }, [videoEnabled]);
  useEffect(() => {
    if (!localStream) {
      return;
    }
    const audioTrack = localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = audioEnabled;
    }
  }, [audioEnabled]);

  const initWebRTC = () => {
    console.log("init WebRTC");
    peerConnection = new RTCPeerConnection(peerConnectionConfig);
    peerConnection.ontrack = (event) => {
      console.log("got remote track");
      let remoteStream = event.streams && event.streams[0];
      if (!remoteStream) {
        if (!remoteStreamRef.current) {
          remoteStreamRef.current = new MediaStream();
        }
        remoteStreamRef.current.addTrack(event.track);
        remoteStream = remoteStreamRef.current;
      } else {
        remoteStreamRef.current = remoteStream;
      }
      if (remoteVideo.current && remoteVideo.current.srcObject !== remoteStream) {
        remoteVideo.current.srcObject = remoteStream;
      }
      setHasConnectRemote(true);
      setStatus("已連線");
    };
    peerConnection.onicecandidate = gotIceCandidate;
    peerConnection.ondatachannel = (e) => {
      // got data channel
      receiveChannel = e.channel;
      receiveChannel.onmessage = onChannelMessage;
    };
    peerConnection.oniceconnectionstatechange = function () {
      if (peerConnection.iceConnectionState === "disconnected") {
        console.log("WebRTC Peer Disconnected");
      }
    };
  };

  const initUserMedia = () => {
    return new Promise((resolve, reject) => {
      const constraints = {
        video: true,
        audio: true,
      };
      serverConnection.onmessage = gotWSMessageFromServer;
      if (navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices
          .getUserMedia(constraints)
          .then((stream) => {
            // After user grant userMedia access
            localStream = stream;
            localVideo.current.srcObject = stream;
            isLocalMediaReadyRef.current = true;
            if (pendingRemoteOfferRef.current) {
              const pendingOffer = pendingRemoteOfferRef.current;
              pendingRemoteOfferRef.current = null;
              remoteSdpQueueRef.current = remoteSdpQueueRef.current
                .then(() => handleRemoteOffer(pendingOffer))
                .catch(errorHandler);
            }
            if (pendingStartAsCallerRef.current) {
              pendingStartAsCallerRef.current = false;
              handleConnect(true);
            }
            resolve();
          })
          .catch((err) => {
            console.log(err);
            // alert("Please enable camera and microphone");
            // history.push("/pool");
          });
      } else {
        alert("Your browser does not support getUserMedia API");
        history.push("/video-chat/pool");
      }
    });
  };

  function ensureLocalTracksAdded() {
    if (!localStream || !peerConnection) {
      return;
    }
    localStream.getTracks().forEach((track) => {
      const hasKindSender = peerConnection
        .getSenders()
        .some((sender) => sender.track && sender.track.kind === track.kind);
      if (!hasKindSender) {
        peerConnection.addTrack(track, localStream);
      }
    });
  }

  const handleConnect = (_isCaller) => {
    if (!peerConnection) return;
    isCaller = _isCaller;

    if (_isCaller) {
      if (!isLocalMediaReadyRef.current) {
        pendingStartAsCallerRef.current = true;
        return;
      }
      if (peerConnection.signalingState !== "stable") {
        return;
      }
      ensureLocalTracksAdded();
      if (!sendChannel || sendChannel.readyState === "closed") {
        sendChannel = peerConnection.createDataChannel("sendDataChannel");
        sendChannel.onopen = (e) => {
          console.log("send channel open");
        };
        sendChannel.onmessage = onChannelMessage;
      }
      peerConnection.createOffer().then(sendDescription).catch(errorHandler);
    }
  };

  function handleVideoDisconnect() {
    setVideoEnabled((prev) => !prev);
  }
  function handleAudioDisconnect() {
    setAudioEnabled((prev) => !prev);
  }
  function handleWebRTCDisconnect() {
    if (!window.confirm("確定要離開視訊聊天室嗎？離開後通話將結束。")) return;
    isLeavingRef.current = true;
    if (serverConnection && serverConnection.readyState === 1) {
      send(PEER_DISCONNECTED_FUNC(uuid));
    }
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }
    if (captureStream) {
      captureStream.getTracks().forEach((track) => track.stop());
    }
    onWebRTCDisconnect();
    history.push("/video-chat/pool");
  }

  async function onWebRTCDisconnect() {
    if (peerConnection && peerConnection.signalingState !== "closed") {
      peerConnection.close();
    }
    setPeerLeft(true);
    setStatus("對方已離開聊天室");
  }

  async function flushPendingIceCandidates() {
    if (
      !peerConnection ||
      !peerConnection.remoteDescription ||
      !peerConnection.remoteDescription.type
    ) {
      return;
    }
    const queuedCandidates = pendingIceCandidatesRef.current.splice(0);
    for (const candidate of queuedCandidates) {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }

  async function handleRemoteOffer(sdp) {
    if (peerConnection.signalingState !== "stable") {
      console.warn(
        "Ignore unexpected remote offer. signalingState:",
        peerConnection.signalingState
      );
      return;
    }
    await peerConnection.setRemoteDescription(new RTCSessionDescription(sdp));
    ensureLocalTracksAdded();
    await flushPendingIceCandidates();
    const answer = await peerConnection.createAnswer();
    await sendDescription(answer);
  }

  async function handleRemoteAnswer(sdp) {
    if (peerConnection.signalingState !== "have-local-offer") {
      console.warn(
        "Ignore stale remote answer. signalingState:",
        peerConnection.signalingState
      );
      return;
    }
    await peerConnection.setRemoteDescription(new RTCSessionDescription(sdp));
    await flushPendingIceCandidates();
  }

  async function gotWSMessageFromServer(message) {
    var signal = JSON.parse(message.data);
    console.log("signal", signal);
    if (signal.event === "Room is Full") {
      //alert("Room is Full");
      return;
    }
    if (signal.event === "NEW_USER_CONNECTED") {
      // TODO: dynamic add WebRTC connection
      dispatch(doUpdatePeers(signal.connectionList));
      return;
    }
    if (signal.event === "PEER_DISCONNECTED") {
      onWebRTCDisconnect();
      return;
    }

    // Ignore messages from ourself
    if (signal.uuid === uuid) return;
    if (!peerConnection) return;
    if (signal.sdp) {
      if (signal.sdp.type === "offer" && isCaller !== true) {
        handleConnect(false);
      }
      if (
        signal.sdp.type === "offer" &&
        isCaller !== true &&
        !isLocalMediaReadyRef.current
      ) {
        pendingRemoteOfferRef.current = signal.sdp;
        return;
      }
      if (isCaller && signal.sdp.type === "offer") {
        return;
      }
      if (!isCaller && signal.sdp.type === "answer") {
        return;
      }
      remoteSdpQueueRef.current = remoteSdpQueueRef.current
        .then(() => {
          if (signal.sdp.type === "offer") {
            return handleRemoteOffer(signal.sdp);
          }
          return handleRemoteAnswer(signal.sdp);
        })
        .catch(errorHandler);
    } else if (signal.ice) {
      if (
        !peerConnection.remoteDescription ||
        !peerConnection.remoteDescription.type
      ) {
        pendingIceCandidatesRef.current.push(signal.ice);
        return;
      }
      peerConnection.addIceCandidate(new RTCIceCandidate(signal.ice)).catch(errorHandler);
    }
  }
  function errorHandler(error) {
    console.error(error);
    // if(isCaller && !reconnecting) {
    //   console.log('isCaller');
    //   handleWebRTCDisconnect();
    //   setReconnecting(true);
    //   setTimeout(() => {
    //     handleConnect(true);
    //     setReconnecting(false);
    //   }, 1000)
    // }
  }
  function sendDescription(description) {
    console.log("got description");
    console.log(description);
    return peerConnection
      .setLocalDescription(description)
      .then(function () {
        send(JSON.stringify({ sdp: peerConnection.localDescription, uuid }));
      })
      .catch(errorHandler);
  }
  function gotIceCandidate(event) {
    if (event.candidate != null) {
      send(JSON.stringify({ ice: event.candidate, uuid }));
    }
  }
  const onChannelMessage = (e) => {
    const data = JSON.parse(e.data);
    if (data.type === "text") {
      setTextareaValue(data.value);
    }
    if (!canvasRef.current) {
      return;
    }
    // canvas drag，這邊記得要把 drawstart 跟 draw 分開，不然會出先遠端畫很快時只有點
    const canvasCtx = canvasRef.current.getContext("2d");
    if (data.type === "drawstart") {
      const { mx, my, color } = data.value;
      canvasCtx.strokeStyle = color;
      canvasCtx.beginPath();
      canvasCtx.moveTo(mx, my);
    }
    if (data.type === "draw") {
      const { lx, ly } = data.value;
      canvasCtx.lineTo(lx, ly);
      canvasCtx.stroke();
    }
    if (data.type === "drawEnd") {
      canvasCtx.strokeStyle = window.currentDrawColor;
    }
    //console.log(`on channel message: ${data.value}`);
  };
  const handleSendTextMessage = (e) => {
    const value = e.target.value;
    setTextareaValue(value);
    sendToChannel("text", value);
  };

  const sendToChannel = (type, value) => {
    try {
      if (receiveChannel) {
        receiveChannel.send(
          JSON.stringify({
            type,
            value,
          })
        );
      } else {
        sendChannel.send(
          JSON.stringify({
            type,
            value,
          })
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  async function handleShareScreen() {
    try {
      captureStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });
      const screenVideoTrack = captureStream.getVideoTracks()[0];
      if (!screenVideoTrack) {
        return;
      }
      const videoSender = peerConnection
        .getSenders()
        .find((sender) => sender.track && sender.track.kind === "video");
      if (videoSender) {
        await videoSender.replaceTrack(screenVideoTrack);
      }
      captureStream.getVideoTracks()[0].onended = () => {
        // Click on browser UI stop sharing button
        console.info("ScreenShare has ended");
        // Switch to camera stream
        localVideo.current.srcObject = localStream;
        const localVideoTrack = localStream && localStream.getVideoTracks()[0];
        if (videoSender && localVideoTrack) {
          videoSender.replaceTrack(localVideoTrack).catch(errorHandler);
        }
      };
      localVideo.current.srcObject = captureStream;
    } catch (err) {
      console.error("Error: " + err);
    }
  }

  return (
    <div className={styles.container}>
      <Prompt
        when={true}
        message={() => {
          if (isLeavingRef.current) return true;
          return "確定要離開視訊聊天室嗎？離開後通話將結束。";
        }}
      />
      {peerLeft && (
        <div className={styles.peerLeftOverlay}>
          <div className={styles.peerLeftCard}>
            <div className={styles.peerLeftIcon}>👋</div>
            <p className={styles.peerLeftText}>對方已離開聊天室</p>
            <button
              type="button"
              className={styles.peerLeftBtn}
              onClick={() => {
                isLeavingRef.current = true;
                history.push("/video-chat/pool");
              }}
            >
              返回配對
            </button>
          </div>
        </div>
      )}
      <header className={styles.headerCard}>
        <div className={styles.headerBrand}>
          <div className={styles.brandTile}>
            <img src={mainSquare} alt="PuiPi Logo" className={styles.mainSquareImage} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>寵物主人視訊聊天室</h1>
            <p className={styles.pageSubtitle}>房間 #{roomPreviewId}</p>
          </div>
        </div>
        <div className={styles.headerTools}>
          <div className={styles.headerHint}>配對採隨機制，盡情交流毛孩、寵物日常</div>
          <ThemeSwitcher />
        </div>
      </header>
      {hasPetTypeSummary && (
        <section className={styles.petTypeSummary} aria-label="寵物類型摘要">
          <div className={styles.petTypeCard}>
            <span className={styles.petTypeLabel}>你選了什麼</span>
            <span className={styles.petTypeValue}>{selfPetType || "未提供"}</span>
          </div>
          <div className={styles.petTypeCard}>
            <span className={styles.petTypeLabel}>對方選了什麼</span>
            <span className={styles.petTypeValue}>{peerPetType || "未提供"}</span>
          </div>
        </section>
      )}
      <div className={styles.toolbar}>
        {/* <button onClick={() => handleConnect(true)}>連線</button> */}
        {/* <button onClick={() => handleShareScreen()}>分享螢幕</button> */}
        <button
          type="button"
          className={`${styles.controlBtn} ${styles.leaveBtn}`}
          onClick={() => {
            handleWebRTCDisconnect();
          }}
        >
          結束通話
        </button>
        <button
          type="button"
          className={styles.controlBtn}
          onClick={() => handleVideoDisconnect()}
        >
          {videoEnabled ? "關閉鏡頭" : "開啟鏡頭"}
        </button>
        <button
          type="button"
          className={styles.controlBtn}
          onClick={() => handleAudioDisconnect()}
        >
          {audioEnabled ? "關閉麥克風" : "開啟麥克風"}
        </button>
        {/* <button onClick={() => handleWebRTCDisconnect()}>斷線</button> */}
        <span
          className={`${styles.statusBadge} ${
            hasConnectRemote ? styles.statusConnected : styles.statusPending
          }`}
        >
          {status}
        </span>
      </div>

      <div className={styles.contentLayout}>
        <section className={styles.videoPanel}>
          <div className={styles.videoArea}>
            <video
              muted
              ref={localVideo}
              autoPlay
              playsInline
              className={styles.videoStream}
            />
            <div className={styles.videoDivider} />
            <video ref={remoteVideo} autoPlay playsInline className={styles.videoStream} />
          </div>
        </section>
        <aside className={styles.chatPanel}>
          <label className={styles.chatLabel} htmlFor="room-text-chat">
            即時共筆
          </label>
          <textarea
            id="room-text-chat"
            className={styles.chatTextarea}
            value={textareaValue}
            onChange={handleSendTextMessage}
            placeholder="輸入內容，雙方會即時同步..."
            maxLength={800}
          />
          <p className={styles.chatHint}>可同步記錄重點，邊聊邊整理內容。</p>
        </aside>
      </div>
      {/* <div className="color-picker"></div>
      <canvas
        className={styles.canvas}
        ref={canvasRef}
        height={400}
        width={400}
      /> */}
      {/* {videoPeers
        .filter((peers) => peers.userId !== uuid)
        .map((peer) => (
          <RemoteVideo key={peer.userId} peer={peer} />
        ))} */}
    </div>
  );
}

export default App;
